import { Koi } from '@ksyos/koi';
import type { IAsset } from '../assets';
import type { ISchemaContainer } from '../ISchemaContainer';
import { phoneNumberRegex } from '../../utils/phoneNumberRegex';
import { assets } from '../assets';

export interface IGtoProcedureCustomControlData {
    areThereAdditionalComplaintsOrSymptons: string;
    areThereAdditionalComplaintsOrSymptonsRemark: string;
    areYouBeingTreated?: string;
    areYouBeingTreatedRemark?: string;
    areYouOnMedication?: string;
    areYouOnMedicationRemark?: string;
    complaints: string;
    complaintsDuration: string;
    complaintsLocation: string;
    complaintsSeverity: string;
    doYouHaveFever: string;
    doYouHaveFeverRemark: string;
    email?: string;
    files: IAsset[];
    haveYouEverBeenHospitalized: string;
    haveYouEverBeenHospitalizedRemark: string;
    organizationName?: string;
    phoneNumber: string;
    practitionerName?: string;
}
export interface IGtoProcedureAddValidated {
    customControlData: IGtoProcedureCustomControlData;
    procedureId?: string;
}

export const step1Schema = {
    schema: Koi.object().keys({
        email: Koi.string().email({ tlds: false }).max(255).optional(),
        phoneNumber: Koi.string().regex(phoneNumberRegex).max(255),
    }),
};

export const step2Schema = {
    schema: Koi.object().keys({
        complaints: Koi.string().max(1000),
        complaintsDuration: Koi.string().max(255),
        complaintsLocation: Koi.string().max(1000),
        complaintsSeverity: Koi.string().max(1000),
        files: assets,
    }),
};

export const step3Schema = {
    schema: Koi.object().keys({
        areThereAdditionalComplaintsOrSymptons: Koi.string().valid('Ja', 'Nee'),
        areThereAdditionalComplaintsOrSymptonsRemark: Koi.when('areThereAdditionalComplaintsOrSymptons', {
            is: 'Ja',
            otherwise: Koi.string().valid(''),
            then: Koi.string().max(1000),
        }),
        doYouHaveFever: Koi.string().valid('Ja', 'Nee'),
        doYouHaveFeverRemark: Koi.when('doYouHaveFever', {
            is: 'Ja',
            otherwise: Koi.string().valid(''),
            then: Koi.string().max(1000),
        }),
        haveYouEverBeenHospitalized: Koi.string().valid('Ja', 'Nee'),
        haveYouEverBeenHospitalizedRemark: Koi.when('haveYouEverBeenHospitalized', {
            is: 'Ja',
            otherwise: Koi.string().valid(''),
            then: Koi.string().max(1000),
        }),
    }),
};

export const step3NoGPInvitationSchema = {
    schema: step3Schema.schema.concat(
        Koi.object({
            areYouBeingTreated: Koi.string().valid('Ja', 'Nee'),
            areYouBeingTreatedRemark: Koi.when('areYouBeingTreated', {
                is: 'Ja',
                otherwise: Koi.string().valid(''),
                then: Koi.string().max(1000),
            }),
            areYouOnMedication: Koi.string().valid('Ja', 'Nee'),
            areYouOnMedicationRemark: Koi.when('areYouOnMedication', {
                is: 'Ja',
                otherwise: Koi.string().valid(''),
                then: Koi.string().max(1000),
            }),
        })
    ),
};

export const step4Schema = {
    schema: Koi.object().keys({
        organizationName: Koi.string().max(255),
        practitionerName: Koi.string().max(255),
    }),
};

export const gtoProcedureSchema: ISchemaContainer<IGtoProcedureAddValidated> = {
    schema: Koi.alternatives().try(
        Koi.object({
            customControlData: Koi.object()
                .concat(step1Schema.schema)
                .concat(
                    Koi.object({
                        email: Koi.any().forbidden(),
                    })
                )
                .concat(step2Schema.schema)
                .concat(step3Schema.schema)
                .options({ presence: 'required' }),
            procedureId: Koi.string().uuid(),
        }).options({ presence: 'required' }),
        Koi.object({
            customControlData: Koi.object()
                .concat(step1Schema.schema)
                .concat(
                    Koi.object({
                        email: Koi.string().email({ tlds: false }).max(255).required(),
                    })
                )
                .concat(step2Schema.schema)
                .concat(step3NoGPInvitationSchema.schema)
                .concat(step4Schema.schema)
                .options({ presence: 'required' }),
        }).options({ presence: 'required' })
    ),
};
